/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState } from 'react';

import type {
  BranchLocation,
  Location,
  LocationTreeWithRoot,
  NodeIndex,
} from './types';
import DefaultBranch from './components/LocationTreeBranch/LocationTreeBranch';
import DefaultLeaf from './components/LocationTreeLeaf/LocationTreeLeaf';
import LocationNode from './components/LocationTreeNode/LocationTreeNode';
import LocationTreeKeyboardController from './components/LocationTreeKeyboardController/LocationTreeKeyboardController';
import { LocationTreeProvider } from './context/LocationTreeContext';
import { LocationTreeProps, LocationProps } from './types';
import ROOT_NODE from './constants';
import { createNodeIndex, editRecursive } from './utils/recursive';
import { isBranch } from './utils/validations';
import { useAutoExpandAncestors } from './hooks/useAutoExpandAncestors/useAutoExpandAncestors';
import normalizeLocationId from './utils/normalizeLocationId';

const LocationTree: React.FC<LocationTreeProps> = ({
  initialTree,
  loadChildren,
  selectedLocations,
  onSelectedChange,
  branch = DefaultBranch,
  leaf = DefaultLeaf,
}) => {
  const [tree, setTree] = useState<LocationTreeWithRoot>(() => {
    const clonedInitialTree = Array.isArray(initialTree)
      ? [...initialTree]
      : [];
    return [{ ...ROOT_NODE, children: clonedInitialTree }];
  });

  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const nodeIndex: NodeIndex = useMemo(() => createNodeIndex(tree), [tree]);

  const updateTree = useCallback(
    (updater: (prev: LocationTreeWithRoot) => LocationTreeWithRoot) => {
      setTree((prevTree: LocationTreeWithRoot) => {
        const newTree = updater(prevTree);
        return newTree;
      });
    },
    []
  );

  const handleToggleOpen = useCallback(
    async (targetBranch: BranchLocation) => {
      if (targetBranch.isOpen) {
        updateTree((prev) => {
          const newTree = editRecursive(prev, {
            ...targetBranch,
            isOpen: false,
          });
          return newTree;
        });

        return;
      }

      if (!targetBranch.isOpen && !targetBranch.hasFetched) {
        updateTree((prev) => {
          const newTree = editRecursive(prev, {
            ...targetBranch,
            isLoading: true,
            error: undefined,
          });
          return newTree;
        });

        try {
          const children = await loadChildren(targetBranch);
          const newBranch: BranchLocation = {
            ...targetBranch,
            children,
            isOpen: true,
            isLoading: false,
            hasFetched: true,
            error: undefined,
          };

          updateTree((prev) => {
            const newTree = editRecursive(prev, newBranch);
            return newTree;
          });
        } catch (error) {
          updateTree((prev) => {
            const newTree = editRecursive(prev, {
              ...targetBranch,
              isLoading: false,
              error,
            });
            return newTree;
          });
        }

        return;
      }

      updateTree((prev) => {
        const newTree = editRecursive(prev, { ...targetBranch, isOpen: true });
        return newTree;
      });
    },
    [updateTree, loadChildren]
  );

  const renderNode = (node: Location, depth: number) => {
    const commonProps: LocationProps = {
      node,
      depth,
      onToggleOpen: handleToggleOpen,
      branch,
      leaf,
    };

    if (!isBranch(node))
      return (
        <LocationNode key={normalizeLocationId(node.id)} {...commonProps} />
      );

    const shouldRenderChildren =
      node.isOpen && Array.isArray(node.children) && node.children.length > 0;

    return (
      <LocationNode key={normalizeLocationId(node.id)} {...commonProps}>
        {shouldRenderChildren
          ? node.children.map((child) => renderNode(child, depth + 1))
          : []}
      </LocationNode>
    );
  };

  useAutoExpandAncestors({
    tree,
    selectedLocations: selectedLocations ?? [],
    nodeIndex,
    onToggleOpen: handleToggleOpen,
  });

  const firstVisibleNodeId = normalizeLocationId(tree[0].children[0]?.id);
  const activeNodeId = focusedNodeId ?? firstVisibleNodeId;

  return (
    <LocationTreeProvider
      selectedLocations={selectedLocations}
      onSelectedChange={onSelectedChange}
      focusedNodeId={activeNodeId}
      setFocusedNodeId={setFocusedNodeId}
    >
      <LocationTreeKeyboardController
        tree={tree}
        nodeIndex={nodeIndex}
        focusedNodeId={activeNodeId}
        setFocusedNodeId={setFocusedNodeId}
        onToggleOpen={handleToggleOpen}
      >
        {tree[0].children.map((node) => renderNode(node, 1))}
      </LocationTreeKeyboardController>
    </LocationTreeProvider>
  );
};

export default LocationTree;
